<template>
  <div layout="column">
    <div layout="row">
      <div class="title mb-3">
        Trainer evaluations
      </div>
    </div>
    <div v-if="loaded">
      <div
        v-if="hasNoForm"
        class="alert"
      >
        <v-icon>mdi-alert-circle-outline</v-icon>
        <span>No trainer's form has been found for this type/concept of session. Please contact an administrator.</span>
      </div>
      <div
        v-if="!fullValidated"
        class="alert"
      >
        <v-icon>mdi-alert-circle-outline</v-icon>
        <span v-if="isTrainer">You can't fill in your individual evaluation until all trainers of the session have validated the Daggesrapport.</span>
        <span v-else>Trainer's validation of the daggesrapport are missing.</span>
      </div>
      <div v-if="isTrainer || isAdminEditingEval">
        <div v-if=" ! currentEval && isAdminEditingEval">
          <div class="mb-3">
            <strong>Who do you want to create the evaluation for ?</strong>
          </div>
          <div v-if="remainingTrainers.length > 0">
            <div id="create-as-admin">
              <v-select
                v-model="evalToBeCreatedForTrainerID"
                :items="remainingTrainers"
                item-text="fullname"
                item-value="id"
                label="Choose a trainer"
                outline
                :hide-details="true"
              />
              <div class="save-section">
                <v-btn
                  depressed
                  color="primary"
                  @click="createEval"
                >
                  Create
                </v-btn>
                <span>ou&nbsp;&nbsp;<a @click="cancelCreation">Cancel</a></span>
              </div>
            </div>
          </div>
          <div v-else>
            <em>All evaluations for this session have already been created, no trainer is missing. If you need to add a new trainer evaluation, you have to add this trainer to the session first</em>
            <div>
              <v-btn
                depressed
                color="primary"
                @click="cancelCreation"
              >
                Back to the list
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else-if="currentEval">
          <v-form
            ref="form"
            v-model="formValid"
            @submit.stop.prevent="save"
          >
            <div class="main-container">
              <div class="form-container">
                <div
                  v-if="isAdminEditingEval"
                  class="legend info-legend mb-4"
                >
                  You are editing the evaluation of <strong>{{ currentEval.trainer.firstname }} {{ currentEval.trainer.lastname }}</strong>
                </div>
                <generic-form
                  :key="`${currentEval.id}-${currentEval.updated_at}`"
                  :evaluation="currentEval"
                  :readonly="isReadonly"
                  @change="updateRapport"
                />
              </div>
              <div class="meta-container">
                <div class="meta">
                  <div v-if="session.application == Consts.APPLICATION.BEESECURE">
                    <h2>School rating</h2>
                    <div class="rating-container">
                      <v-rating
                        v-model="currentEval.school_mark"
                        hover
                        :length="5"
                        :size="32"
                        active-color="primary"
                        background-color="primary"
                      />
                      <a @click="currentEval.school_mark = null">Empty !</a>
                    </div>
                  </div>
                  <h2>Working time</h2>
                  <div>
                    <div>
                      <div class="legend no-border mb-1">
                        in decimal (e.g. 2h30 --> 2.5)
                      </div>
                      <v-text-field
                        v-model="currentEval.working_time"
                        label="Total working time"
                        outline
                        :readonly="isReadonly"
                        :rules="[v => !!v || 'This field is required']"
                      />
                    </div>
                    <div v-if="working_time > sessionTotalTime">
                      <v-textarea
                        v-model="currentEval.working_time_exceeding_reason"
                        label="Reason of this time exceeding"
                        :readonly="isReadonly"
                        rows="4"
                        :hide-details="true"
                      />
                      <div class="legend">
                        The total time estimated of this session is {{ sessionTotalTime }} hours
                      </div>
                    </div>
                  </div>
                  <div class="save-section mb-3">
                    <v-btn
                      depressed
                      color="primary"
                      :disabled="isReadonly"
                      @click="save"
                    >
                      Save form
                    </v-btn>
                    <div v-if="isAdminEditingEval">
                      <span>ou&nbsp;&nbsp;<a @click="cancelCreation">Cancel</a></span>
                    </div>
                  </div>
                  <div v-if="editedTrainer">
                    <h2>Personal data</h2>
                    <div class="mb-3">
                      <em v-if="!isAdminEditingEval">Please check that your personal data is complete and up to date. This data is required to establish your payments.</em>
                      <em v-else>Please check that the trainer's details are complete and up to date.</em>
                    </div>
                    <div class="personal-data">
                      <div class="personal-header">
                        <div class="avatar">
                          <v-icon large>
                            mdi-account-circle
                          </v-icon>
                        </div>
                        <h3>{{ editedTrainer.firstname }} {{ editedTrainer.lastname }}</h3>
                        <template v-if="isAdmin || (editedTrainer.id == user)">
                          <a
                            v-if="!editionOfTrainerData && !isReadonly"
                            title="Edit"
                            @click="startEditionOfTrainer"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </a>
                          <a
                            v-else-if="!isReadonly"
                            title="Cancel edition"
                            @click="editionOfTrainerData = false"
                          >
                            <v-icon small>mdi-close-thick</v-icon>
                          </a>
                        </template>
                      </div>
                      <div
                        v-if="!editionOfTrainerData"
                        class="data"
                      >
                        <div class="sub-data sub-grow">
                          <h4>Address</h4>
                          <div>
                            <span v-if="editedTrainer.streetnumber">{{ editedTrainer.streetnumber }}</span>
                            <span
                              v-else
                              class="missing"
                            >Street number missing</span>,
                            <span v-if="editedTrainer.streetname">{{ editedTrainer.streetname }}</span>
                            <span
                              v-else
                              class="missing"
                            >Street name missing</span>
                          </div>
                          <div>
                            <span v-if="editedTrainer.postalcode">{{ editedTrainer.postalcode }}</span>
                            <span
                              v-else
                              class="missing"
                            >Postal code missing</span>&nbsp;-&nbsp;
                            <span v-if="editedTrainer.city">{{ editedTrainer.city }}</span>
                            <span
                              v-else
                              class="missing"
                            >City missing</span>
                          </div>
                          <div>
                            <span v-if="editedTrainer.country">{{ editedTrainer.country.name }}</span>
                            <span
                              v-else
                              class="missing"
                            >Country missing</span>
                          </div>
                        </div>
                        <div class="sub-data">
                          <h4>Payment info</h4>
                          <table>
                            <tr>
                              <td class="label">
                                <strong>Matricule</strong>
                              </td>
                              <td>
                                <span v-if="editedTrainer.matricule">{{ editedTrainer.matricule }}</span>
                                <span
                                  v-else
                                  class="missing"
                                >missing</span>
                              </td>
                            </tr>
                            <tr>
                              <td class="label">
                                <strong>IBAN</strong>
                              </td>
                              <td>
                                <span v-if="editedTrainer.iban">{{ editedTrainer.iban }}</span>
                                <span
                                  v-else
                                  class="missing"
                                >missing</span>
                              </td>
                            </tr>
                            <tr>
                              <td class="label">
                                <strong>BIC</strong>
                              </td>
                              <td>
                                <span v-if="editedTrainer.bic">{{ editedTrainer.bic }}</span>
                                <span
                                  v-else
                                  class="missing"
                                >missing</span>
                              </td>
                            </tr>
                          </table>
                        </div>
                      </div>
                      <div v-else>
                        <div class="form-row">
                          <h4>Address</h4>
                        </div>
                        <div class="form-row">
                          <v-select
                            v-model="clonedTrainerForEdition.country_id"
                            outline
                            label="Country of residence"
                            :rules="[v => !!v || 'This field is required' ]"
                            :items="countries"
                            item-value="iso"
                            item-text="name"
                          />
                        </div>
                        <div v-if="clonedTrainerForEdition.country_id == 'lu'">
                          <div layout="row">
                            <v-menu
                              v-model="menuVisible"
                              :close-on-content-click="false"
                              :nudge-width="200"
                              :offset-y="true"
                              max-height="300"
                              open-on-focus
                              transition="fade-transition"
                            >
                              <template #activator="{ on }">
                                <v-text-field
                                  ref="searchField"
                                  v-model="search"
                                  label="Rechercher une rue luxembourgeoise"
                                  :loading="loading"
                                  clearable
                                  hide-details
                                  :disabled="ignore_lux_cities"
                                  @input="onSearchInput"
                                  @focus="menuVisible = true"
                                  v-on="on"
                                />
                              </template>
                              <v-card flat>
                                <v-list v-if="streets.length > 0">
                                  <v-list-tile
                                    v-for="street in streets"
                                    :key="street.id"
                                    @click="onStreetSelected(street)"
                                  >
                                    <v-list-tile-content>
                                      <v-list-tile-title>{{ street.name }}</v-list-tile-title>
                                    </v-list-tile-content>
                                  </v-list-tile>
                                </v-list>

                                <v-list v-else>
                                  <v-list-tile>
                                    <v-list-tile-content>
                                      <v-list-tile-title>Aucun résultat</v-list-tile-title>
                                    </v-list-tile-content>
                                  </v-list-tile>
                                </v-list>
                              </v-card>
                            </v-menu>
                          </div>
                          <div
                            layout="row"
                            style="margin-bottom: 20px"
                          >
                            <v-checkbox
                              v-model="ignore_lux_cities"
                              label="Je n'ai pas trouvé l'adresse"
                              hide-details
                              @change="onChangeIgnoreLuxCities"
                            />
                          </div>
                        </div>
                        <div class="form-row">
                          <v-text-field
                            v-model="clonedTrainerForEdition.streetnumber"
                            outline
                            label="Street number"
                            class=" w75 mr-1"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                          />
                          <v-text-field
                            v-model="clonedTrainerForEdition.streetname"
                            outline
                            label="Street name"
                            class="grow"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                            :readonly="!ignore_lux_cities"
                          />
                        </div>
                        <div class="form-row">
                          <v-text-field
                            v-model="clonedTrainerForEdition.postalcode"
                            outline
                            label="Postal code"
                            class=" w75 mr-1"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                            :readonly="!ignore_lux_cities"
                          />
                          <v-text-field
                            v-model="clonedTrainerForEdition.city"
                            outline
                            label="City"
                            class="grow"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                            :readonly="!ignore_lux_cities"
                          />
                        </div>
                        <div class="form-row">
                          <h4>Payment info</h4>
                        </div>
                        <div class="form-row">
                          <v-text-field
                            v-model="clonedTrainerForEdition.matricule"
                            outline
                            label="Matricule number"
                            class="grow"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                          />
                        </div>
                        <div class="form-row">
                          <v-text-field
                            v-model="clonedTrainerForEdition.iban"
                            outline
                            label="IBAN"
                            class="grow mr-1"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                          />
                          <v-text-field
                            v-model="clonedTrainerForEdition.bic"
                            outline
                            label="BIC"
                            class="w75"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                          />
                        </div>
                        <div class="form-row center">
                          <v-btn
                            depressed
                            color="primary"
                            @click="updatePersonalInfo"
                          >
                            Update
                          </v-btn>
                          <span>ou&nbsp;&nbsp;<a @click="cancelEditionOfTrainer">Cancel</a></span>
                        </div>
                      </div> <!-- fin form personal data -->
                    </div>
                  </div>
                  <div class="mt-3">
                    <h2>Mileage expenses</h2>
                    <v-checkbox
                      v-model="mileage.has_kms"
                      label="The session implied mileage expenses"
                      color="primary"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      :readonly="isReadonly"
                      @change="initMileageData"
                    />
                    <div v-if="mileage.has_kms">
                      <v-checkbox
                        v-model="mileage.has_foreign_kms"
                        label="The whole journey was inside Luxembourg"
                        color="primary"
                        :true-value="0"
                        :false-value="1"
                        :readonly="isReadonly"
                        hide-details
                        @change="initMileageForeignData"
                      />
                      <div v-if="mileage.has_foreign_kms">
                        <div class="mt-2">
                          <v-text-field
                            v-model="mileage.kms"
                            outline
                            label="Kilometers traveled"
                            :readonly="isReadonly"
                            :rules="[v => !!v || 'This field is required']"
                            :hide-details="true"
                          />
                        </div>
                        <div>
                          <v-text-field
                            v-model="mileage.kms_receipt.name"
                            label="Receipt"
                            :readonly="isReadonly"
                            prepend-icon="mdi-attachment"
                            @click="pickFile()"
                          />
                          <input
                            ref="receiptFile"
                            type="file"
                            :readonly="isReadonly"
                            style="display: none"
                            accept="application/pdf"
                            @change="onFilePicked($event)"
                          >
                        </div>
                        <a
                          v-if="mileage.kms_receipt_id"
                          class="underlined"
                          @click="downloadReceipt()"
                        >Download receipt</a>
                      </div>
                      <div
                        v-else
                        class="mt-3"
                      >
                        <div
                          v-for="(s, idx) in mileage.steps"
                          :key="idx"
                          class="flex-v-center mb-2"
                        >
                          <v-icon>mdi-sign-direction</v-icon>
                          <v-autocomplete
                            :key="idx"
                            v-model="s.city_id"
                            :items="cities"
                            item-text="name"
                            item-value="id"
                            :readonly="isReadonly"
                            :label="`Step ${idx+1}`"
                            outline
                            :rules="[
                              v => !!v || 'This field is required',
                            ]"
                            :hide-details="true"
                          />
                          <a
                            v-if="!isReadonly"
                            class="remover"
                            title="Remove this step"
                            @click="mileage.steps.splice(idx, 1)"
                          ><v-icon>mdi-close</v-icon></a>
                        </div>
                        <a
                          v-if="!isReadonly"
                          class="underlined mb-80"
                          @click="addNewStep"
                        >Add a new step</a>
                      </div>
                      <div class="form-row center mt-3">
                        <v-btn
                          depressed
                          color="primary"
                          :disabled="!mileageSavable || isReadonly"
                          @click="saveMileageData"
                        >
                          Save mileage expenses
                        </v-btn>
                        <span>ou&nbsp;&nbsp;<a @click="resetMileageData(null)">Cancel</a></span>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="!!currentEval.saved_once && currentEval.saved_once != '0'"
                    class="mt-3"
                  >
                    <h2>Validation</h2>
                    <div
                      class="legend"
                      :class="{'validated': currentEval.is_validated}"
                    >
                      <strong>Info: </strong>
                      <span v-if="!currentEval.is_validated">Your indemnities won't be paid until you validate this evaluation</span>
                      <span v-else>If all the trainers/AS in this session have validated their individual evaluations, your payment will be processed as quick as possible</span>
                    </div>
                    <div class="flex-center mt-2">
                      <v-btn
                        depressed
                        color="#82e0aa"
                        :disabled="!!currentEval.is_validated"
                        :title="currentEval.is_validated ? 'This evaluation is already validated' : ''"
                        @click="validateEvaluation"
                      >
                        Validate this evaluation
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-form>
        </div>
      </div>
      <div
        v-else-if="fullValidated"
        layout-gt-md="row"
        layout-align="start stretch"
        layout-wrap
        style="width: 100%"
      >
        <trainer-evals
          :application="session.application"
          :evaluations="evaluationsByTime"
          :is-admin="isAdmin"
          :is-coord="isCoord"
          :session="session"
          :session-default-times="sessionTotalTimePerTrainer"
          @edit="(e) => adminEditEval(e)"
          @eval:delete="(e) => deleteEval(e)"
        />
        <v-card
          v-if="canAddNewEval()"
          ripple
          class="ml-5"
          style="background: unset; height: fit-content"
          flat
        >
          <v-card-title primary-title>
            <v-avatar
              size="80"
              style="cursor: pointer"
              @click="newEval()"
            >
              <v-icon
                size="60"
                :class="{'circle-container': true, 'primary': true}"
                color="#ffffff"
              >
                mdi-plus
              </v-icon>
            </v-avatar>
          </v-card-title>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import Consts from "@/consts"
import Sessions from '@/api/sessions'
import Users from '@/api/users'
import Params from "@/api/params"
import DateMixin from '@/mixins/date'
import Auth from '@/plugins/auth'
import GenericForm from "@/components/sessions/evals/GenericForm/Form.vue"
import TrainerEvals from "@/components/sessions/evals/TrainerEvalsList.vue"
import FormDataMixin from '@/mixins/formdata'
import axios from 'axios'
import Vue from 'vue'

export default {
  components: { GenericForm, TrainerEvals },
  mixins: [DateMixin, FormDataMixin],
  props: ['session', 'isAdmin', 'isCoord'],
  data () {
    return {
      hasNoForm: false,
      validations: [], //du daggesrapport
      evaluations: [],
      currentEval: null,
      user: null,
      fullValidated: false,
      loaded: false,
      formValid: false,
      isAdminEditingEval: false,
      evalToBeCreatedForTrainerID: null, //utilisé pour la création par l'admin
      Consts,
      editedTrainer: null,
      clonedTrainerForEdition: null,
      editionOfTrainerData: false,
      countries: [],
      cities: [],
      mileage: {//on en fait un objet séparé du currentEval pour dissocier les save
        has_kms: 0,
        has_foreign_kms: 0,
        kms_receipt_id: null,
        kms: null,
        steps: [],
        kms_receipt: {
          name: "",
          data: null
        }
      },
      //Pour les adresses
      menuVisible: false,
      search: '',
      loading: false,
      ignore_lux_cities: false,
      streets: [],
      searchTimeout: null
    }
  },
  computed: {
    isTrainer() {
      return !this.isAdmin && !this.isCoord
    },
    sessionTotalTime() {
      if( ! this.currentEval) {
        return 0
      }

      return this.session.trainers_presences.filter(tp => tp.user_id == this.currentEval.user_id)
                                    .reduce((acc, e) => acc += e.presences.reduce((acc2, p) => acc2 += parseFloat(p.session_date.total_time), 0), 0)
    },
    sessionTotalTimePerTrainer() {
      let totalTimes = {}
       this.session.trainers_presences.forEach(tp => {
        if( ! totalTimes.hasOwnProperty(tp.user_id)) {
          totalTimes[tp.user_id] = 0
        }
        totalTimes[tp.user_id] += tp.presences.reduce((acc2, p) => acc2 += parseFloat(p.session_date.total_time), 0)
      })
      return totalTimes

    },
    working_time() { //pour pouvoir fair le watcher dessus
      return this.currentEval ? parseFloat((""+this.currentEval.working_time).replace(',', '.')) : 0
    },
    remainingTrainers() {
      return this.session.trainers.filter(t => {
        return t.role == Consts.TRAINER && ! this.evaluations.find(e => e.user_id == t.id)
      }).map(t => {
        return {id: t.id, fullname: `${t.firstname} ${t.lastname}`}
      })
    },
    evaluationsByTime() {
      return this.evaluations.toSorted((a, b) => {
        if(a.created_at < b.created_at) {
          return -1
        }
        else if(a.created_at > b.created_at) {
          return 1
        }
        return 0
      })
    },
    isTrainerPersonalDataComplete() {
      return ! this.editedTrainer || (
          (this.editedTrainer.streetnumber && this.editedTrainer.streetnumber.trim() != "") &&
          (this.editedTrainer.streetname && this.editedTrainer.streetname.trim() != "") &&
          (this.editedTrainer.postalcode && (""+this.editedTrainer.postalcode).trim() != "") &&
          (this.editedTrainer.city && this.editedTrainer.city.trim() != "") &&
          this.editedTrainer.country_id  &&
          (this.editedTrainer.matricule && this.editedTrainer.matricule.trim() != "") &&
          (this.editedTrainer.iban && this.editedTrainer.iban.trim() != "") &&
          (this.editedTrainer.bic && this.editedTrainer.bic.trim() != "")
        )
    },
    mileageSavable() {
      return this.mileage.has_foreign_kms || (this.mileage.steps && this.mileage.steps.length >= 2 && this.mileage.steps.reduce((acc, s) => acc = acc && s.city_id != null, true))
    },
    isReadonly () {
      return this.session.status == Consts.TOCONTROL || this.session.status == Consts.CLOSED
    }
  },
  watch: {
    working_time() {
      if(this.currentEval && this.working_time <= this.sessionTotalTime) {
        this.currentEval.working_time_exceeding_reason = ""
      }
    },
    async currentEval() { //Cyril : j'aime moyen ça mais c'est le seul moyen de couvrir tous les cas où currentEval change, y compris dans le futur, pour pas oublier de charger l'utilisateur dont on va pouvoir modifier les données personnelles
      if(this.currentEval) {
        const {data} = await Users.show(this.currentEval.trainer.id)
        this.editedTrainer = data
        this.resetMileageData()
      }
      else {
        this.editedTrainer = null
      }
    }
  },

  mounted () {
    this.user = Auth.user.id
    this.fetch()
  },
  methods: {
    async fetch () {
      try {
        const {data} = await Sessions.getDaggesrapportValidations(this.$route.params.id)
        this.validations = data
        
        let nbTrainers = 0
        this.session.trainers.forEach(u => {
          if(u.role == Consts.TRAINER){
            nbTrainers++
          }
        })

        /**
         * Cyril : note à propos du <= dans la condition suivante
         * c'est parce qu'on peut supprimer un trainer après qu'il ait validé le daggesrapport
         */
        this.fullValidated = nbTrainers <= this.validations.length
      }
      catch(e) {
        if(e.response && e.response.status == 404) {
          this.fullValidated = false
        }
      }
      /**
       * on doit tenir compte du profil de l'utilisateur
       * soit on est admin/coord alors on va tout chercher
       * soit on est trainer alors on va chercher (ou créer à la volée) l'évaluation de ce trainer
       */

      if(this.fullValidated || ! this.isTrainer) {
        const {data: evaluations} = await Sessions.getTrainerEvals(this.$route.params.id)
        this.evaluations = evaluations
        this.currentEval = null
        if(this.isTrainer) {
          this.currentEval = this.evaluations.find(e => e.user_id == Auth.user.id)
          if( ! this.currentEval) { //on la crée
            try {
              const {data: cEval} = await Sessions.createTrainerEval(this.session.id, {})
              this.currentEval = cEval
              this.evaluations.push(this.currentEval) //on met à jour la liste au cas où
            }
            catch(e) {
              if(e.response && e.response.status == 422) {
                this.hasNoForm = true
                this.currentEval = null
              }
              this.loaded = true
            }
          }
          if(this.currentEval) {
            this.currentEval.working_time = (""+this.currentEval.working_time).replace('.', ',')
            this.currentEval.kms = (""+this.currentEval.kms).replace('.', ',')
          }
        }
      }

      const {data: countries} = await Params.getList("countries", {sortBy: "name"})
      this.countries = countries

      const {data: cities} = await Params.getList('cities', {is_from_geoportail: 1})
      this.cities = cities

      this.loaded = true
    },
    updateRapport(evaluation) {
      const wTime = this.currentEval.working_time
      const exceeding = this.currentEval.working_time_exceeding_reason
      const rating = this.currentEval.school_mark

      this.currentEval = evaluation

      this.currentEval.working_time = wTime
      this.currentEval.exceeding = exceeding
      this.currentEval.school_mark = rating
    },
    async save() {
      if (!this.$refs.form.validate()) {
        this.$snotify.error('Please check the content of your form')
        return;
      }
      if(!this.isTrainerPersonalDataComplete) {
        this.$snotify.error('Personal data of the trainer is incomplete')
        return;
      }

      try {
        let cloneEval = structuredClone(this.currentEval)
        cloneEval.working_time = (""+cloneEval.working_time).replace(',', '.')
        const {data} = await Sessions.updateTrainerEval(this.session.id, this.currentEval.id, cloneEval)
        cloneEval = null // mémoire

        this.currentEval = data
        this.currentEval.working_time = (""+this.currentEval.working_time).replace('.', ',')
        this.evaluations.splice(this.evaluations.indexOf(this.evaluations.find(e => e.id == this.currentEval.id)), 1, data)
        this.$snotify.success('The evaluation has been saved successfully')
      }
      catch(e) {
        //intercepted by axios
        console.log(e)
      }
    },
    adminEditEval(e) {
      this.isAdminEditingEval = true
      this.currentEval = e
      this.currentEval.working_time = (""+this.currentEval.working_time).replace('.', ',')
    },
    canAddNewEval () {
      return this.isCoord || this.isAdmin
    },
    newEval() {
      this.currentEval = null
      this.isAdminEditingEval = true
      this.evalToBeCreatedForTrainerID = this.remainingTrainers.length > 1  || this.remainingTrainers.length == 0 ? null : this.remainingTrainers[0].id
    },
    async createEval() {
      if( ! this.evalToBeCreatedForTrainerID) {
        this.$snotify.error('Please choose a trainer')
        return
      }
      const {data} = await Sessions.createTrainerEval(this.session.id, {trainer_id: this.evalToBeCreatedForTrainerID})
      this.currentEval = data
      this.isAdminEditingEval = true
      this.evaluations.push(data)
      this.evalToBeCreatedForTrainerID = null
      this.$snotify.success('Evaluation created with success')
    },
    cancelCreation() {
      this.evalToBeCreatedForTrainerID = null
      this.isAdminEditingEval = false
      this.cancelEditionOfTrainer()
    },
    startEditionOfTrainer() {
      this.clonedTrainerForEdition = structuredClone(this.editedTrainer)
      this.ignore_lux_cities = this.clonedTrainerForEdition.country_id != 'lu' || ! this.clonedTrainerForEdition.street_id
      this.editionOfTrainerData = true
    },
    cancelEditionOfTrainer() {
      this.clonedTrainerForEdition = null
      this.editionOfTrainerData = false
    },
    async updatePersonalInfo() {
      if(this.clonedTrainerForEdition.streetnumber.trim() == "" ||
          this.clonedTrainerForEdition.streetname.trim() == "" ||
          (""+this.clonedTrainerForEdition.postalcode).trim() == "" ||
          this.clonedTrainerForEdition.city.trim() == "" ||
          ! this.clonedTrainerForEdition.country_id ||
          this.clonedTrainerForEdition.matricule.trim() == "" ||
          this.clonedTrainerForEdition.iban.trim() == "" ||
          this.clonedTrainerForEdition.bic.trim() == "") {
        this.$snotify.error('All field are required')
        return;
      }

      let res = null
      if(this.isTrainer) {
        res = await Users.updatePersonalInfo(this.clonedTrainerForEdition)
      }
      else { //que pour les admins
        res = await Users.updateParamsUserInfo(this.clonedTrainerForEdition.id, this.clonedTrainerForEdition)
      }
      this.editedTrainer = res.data
      this.cancelEditionOfTrainer()
      this.$snotify.success('The profile has been successfully updated')
    },
    initMileageData() {
      if(this.mileage.has_kms) {
        this.mileage.has_foreign_kms = 0
        this.mileage.steps.splice(0, this.mileage.steps.length, {city_id: null})
      }
    },
    async initMileageForeignData() {
      if(this.mileage.has_foreign_kms) {
        this.mileage.steps.splice(0) //on vide
        this.mileage.kms = 0
      }
      else {
        this.mileage.steps.splice(0, this.mileage.steps.length, {city_id: null})
      }
    },
    addNewStep() {
      this.mileage.steps.push({city_id: null})
    },
    async saveMileageData() {
      if(this.mileage.has_foreign_kms && !this.currentEval.kms_receipt_id && !(this.mileage.kms_receipt.data && this.mileage.kms_receipt.name)) {
        this.$snotify.error('The receipt is required')
        return
      }
      const body = structuredClone(this.mileage)
      body.kms = (""+body.kms).replace(',', '.')

      let formData = new FormData();
      if (this.mileage.kms_receipt.data && this.mileage.kms_receipt.name) {
          formData.append("kms_receipt", this.mileage.kms_receipt.data, this.mileage.kms_receipt.name);
      }
      this._convertObjectToFormData(formData, body);

      const {data} = await Sessions.updateMileageKms(this.session.id, this.currentEval.id, formData)
      this.resetMileageData(data)
      //on doit aussi mettre à jour l'évaluation en cours d'édition
      this.updateMileageDataOnEval(data)
      this.$snotify.success('Your journey has been saved successfully')

    },
    resetMileageData(evaluation = null) {
      this.mileage.has_kms = ! evaluation ? this.currentEval.has_kms : evaluation.has_kms
      if(this.mileage.has_kms == 'true' || this.mileage.has_kms == '1') {
        this.mileage.has_kms = 1
      }
      else if(this.mileage.has_kms == 'false' || this.mileage.has_kms == '0') {
        this.mileage.has_kms = 0
      }
      this.mileage.has_foreign_kms = ! evaluation ? this.currentEval.has_foreign_kms : evaluation.has_foreign_kms
      if(this.mileage.has_foreign_kms == 'true' || this.mileage.has_foreign_kms == '1') {
        this.mileage.has_foreign_kms = 1
      }
      else if(this.mileage.has_foreign_kms == 'false' || this.mileage.has_foreign_kms == '0') {
        this.mileage.has_foreign_kms = 0
      }
      this.mileage.kms = ! evaluation ? (""+this.currentEval.kms).replace('.', ',') : (""+evaluation.kms).replace('.', ',')
      this.mileage.steps = ! evaluation ? (this.currentEval.steps ? structuredClone(this.currentEval.steps) : []) : (evaluation.steps ? structuredClone(evaluation.steps) : [])
      this.mileage.kms_receipt_id = ! evaluation ? this.currentEval.kms_receipt_id : evaluation.kms_receipt_id
    },
    updateMileageDataOnEval(data) {
      this.currentEval.has_kms = data.has_kms
      this.currentEval.has_foreign_kms = (data.has_foreign_kms || data.has_foreign_kms == 'true' || data.has_foreign_kms == '1') ? 1 : 0
      this.currentEval.kms = (""+data.kms).replace('.', ',')
      this.currentEval.steps = data.steps
      this.currentEval.kms_receipt_id = data.kms_receipt_id
      if(data.receipt) {
        if( ! this.currentEval.hasOwnProperty('receipt')) {
          Vue.set(this.currentEval, 'receipt', {name: data.receipt.name})
        }
        else {
          this.currentEval.receipt.name = data.receipt.name
        }
        this.mileage.kms_receipt.name = ""
      }
    },
    pickFile() {
      this.$refs['receiptFile'].click();
    },
    onFilePicked(e) {
      const files = e.target.files;
      if (files[0] !== undefined) {
        this.mileage.kms_receipt.data = files[0];
        this.mileage.kms_receipt.name = files[0].name;
      }
    },
    downloadReceipt () {
      let a = document.createElement("a");
      a.style = "display: none";
      document.body.appendChild(a);

      axios.get(`/sessions/${this.$route.params.id}/trainers-evaluations/${this.currentEval.id}/receipt`, {responseType: 'blob'}).then(res => {
        let blob = new Blob([res.data], {type: "octet/stream"});

        if (window.navigator && window.navigator.msSaveOrOpenBlob){
          window.navigator.msSaveBlob(blob, this.currentEval.receipt.name); //il faudra aller chercher le vrai nom du fichier
        }
        else
        {
          let url = window.URL.createObjectURL(blob);
          a.href = url;
          a.download = this.currentEval.receipt.name
          a.click();
          window.URL.revokeObjectURL(url);
        }
      })
    },
    async validateEvaluation() {
      const {data} = await Sessions.validateTrainerEvaluation(this.session.id, this.currentEval.id)
      this.currentEval.is_validated = !!data.is_validated
      this.evaluations.splice(this.evaluations.indexOf(this.evaluations.find(e => e.id == this.currentEval.id)), 1, this.currentEval)
      this.$emit('ask-session-refresh')

    },
    // ----------------- Pour la gestion de l'adresse -----------------
    onChangeCountry() {
      if(this.clonedTrainerForEdition.country_id == 'lu') {
        this.ignore_lux_cities = false
      }
      else {
        this.ignore_lux_cities = true
        this.clonedTrainerForEdition.city_id = null
        this.clonedTrainerForEdition.street_id = null
      }
    },
    onSearchInput(val) {
      if (!val || val.length < 3) {
        this.streets = [];
        this.menuVisible = false;
        return;
      }

      this.loading = true;
      this.menuVisible = true
      clearTimeout(this.searchTimeout) //debounce

      this.searchTimeout = setTimeout(() => {
        this.onSearchStreet(val)
      }, 500)
    },
    async onSearchStreet(search) {
      this.streets.splice(0)
      const {data} = await Params.getList("streets", {search})
      this.streets.push(...data)
      this.loading = false
    },
    onStreetSelected(street) {
      this.clonedTrainerForEdition.street_id = street.id ?? null
      if(this.clonedTrainerForEdition.street_id) {
        this.clonedTrainerForEdition.city_id = street.city_id
        this.clonedTrainerForEdition.streetname = street.streetname
        this.clonedTrainerForEdition.postalcode = street.postalcode
        this.clonedTrainerForEdition.city = street.cityname
      }
      else {
        this.clonedTrainerForEdition.city_id = null
        this.clonedTrainerForEdition.streetname = ""
        this.clonedTrainerForEdition.postalcode = ""
        this.clonedTrainerForEdition.city = null
      }

      this.search = street.name;
      this.menuVisible = false;
    },
    onChangeIgnoreLuxCities() {
      this.clonedTrainerForEdition.street_id = null
      this.clonedTrainerForEdition.city_id = null
    },
    async deleteEval(eid) {
      await Sessions.deleteTrainerEvaluation(this.session.id, eid)
      this.evaluations.splice(this.evaluations.indexOf(this.evaluations.find(f => f.id == eid)), 1)
      this.$snotify.success('The evaluation has been successfully deleted')
      this.$emit('ask-session-refresh')
    }
  },
}
</script>

<style scoped lang="sass">
  .alert
    padding: 2em
    border-radius: 9px
    border: 2px solid tomato
    color: tomato
    width: 100%
    font-size: 1.2rem
    display: flex
    flex-direction: row
    align-items: center

    i.v-icon
      font-size: 60px
      color: tomato
      margin-right: 20px

  .legend
    border-left: 5px solid tomato
    padding-left: 10px
    color: #999999
    margin-top: 10px

    &.no-border
      border-left: 5px solid #C1C1C1

    &.info-legend
      border-left: 5px solid #5dade2
      padding: 10px 0px 10px 10px
      font-size: 1.2em

  form
    width: 100%

  .main-container
    width: 100%
    display: flex
    flex-direction: row
    align-items: top
    flex-wrap: wrap-reverse

    .form-container
      flex: 1 1 500px
      min-width: 500px
      background-color: white
      padding: 24px
      border-radius: 9px

    .meta-container
      width: 400px
      margin-left: 24px
      position: relative

      .meta
        position: sticky
        top: 0px
        padding-top: 20px


      .save-section
        display: flex
        flex-direction: row
        justify-content: center
        align-items: center

  .rating-container
    display: flex
    flex-direction: row
    align-items: center

    > a
      text-decoration: underline
      cursor: pointer
      margin-left: 20px

  #create-as-admin
    display: flex
    align-items: center
    justify-content: flex-start
    width: 30%

  .personal-data
    border-radius: 9px
    background-color: white
    padding: 10px

    .personal-header
      display: flex
      align-items: center

      h3
        flex-grow: 1

      .avatar i
        color: #f4b944
        margin-right: 10px

      a
        cursor: pointer
        display: flex
        width: 32px
        height: 32px
        border-radius: 50%
        justify-content: center
        align-items: center

        i
          color: #f4b944

        &:hover
          background-color: #F1F1F1
    .data
      display: flex
      flex-direction: row
      align-items: flex-start

      .sub-data
        margin: 5px

        .missing
          color: tomato
          font-style: italic

        td
          vertical-align: top

        td.label
          width: 70px

        &.sub-grow
          flex-grow: 1
          min-width: 120px

    h4
      color: #AAAAAA
      margin-bottom: 10px
      font-size: 1.1em

  .form-row
    display: flex
    flex-direction: row
    margin-bottom: 5px
    align-items: center

    .w50
      width: 50px
    .w75
      width: 75px
    .mr-1
      margin-right: 10px
    .grow
      flex-grow: 1

    &.center
      justify-content: center

  .flex-v-center
    display: flex
    flex-direction: row
    align-items: center

  .flex-center
    display: flex
    flex-direction: row
    justify-content: center

  .underlined
    text-decoration: underline
    cursor: pointer

  .remover
    cursor: pointer
    &:hover
      i
        color: tomato

  .mb-80
    margin-bottom: 80px

  .legend.validated
    border-left: 5px solid #82e0aa
</style>
